/**
 * Purchase Order Management Page
 * Create and manage purchase orders for stock replenishment
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth, formatISTDate } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Plus, Download, Search, Trash2, Package,
  Calendar, User, Building2, ChevronRight, X, Check, Edit, Save, History
} from 'lucide-react';

const STOCK_CATEGORIES = ['Plywood', 'Blockboard', 'Doors', 'MDF', 'WPC/PVC', 'Laminates', 'Veneers', 'Hardware', 'Adhesives', 'ACP'];

// Map the product's stored `rate_type` to the PO line unit. This is what
// staff sees in the qty column — they shouldn't pick a unit manually because
// it's already defined by how the product is configured in Product Management.
function rateTypeToUnit(rateType) {
  const rt = (rateType || '').toLowerCase();
  if (rt.includes('sq ft') || rt.includes('sqft') || rt.includes('sheet')) return 'SHT';
  if (rt.includes('running feet') || rt.includes('rft')) return 'RFT';
  if (rt.includes('kg')) return 'KG';
  if (rt.includes('bundle')) return 'BDL';
  if (rt.includes('box')) return 'BOX';
  if (rt.includes('set')) return 'SET';
  if (rt.includes('piece') || rt.includes('nos')) return 'NOS';
  return 'NOS';
}

// Plywood-style categories store both thickness AND size — show them as a
// cartesian product. Other categories store at most one of the two.
const PLYWOOD_STYLE = new Set([
  'Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC', 'ACP', 'Wooden Flooring',
]);
// Adhesives reuse the `thickness` schema field to store weight (e.g., "1KG",
// "500g") — that's the variant axis. No "size".
const WEIGHT_BASED = new Set(['Adhesives', 'Screws/Patti']);

/**
 * Given a deduped `product` (one row from `getUniqueProducts`) and its
 * category, return the list of selectable variant rows. Each row has a stable
 * `key`, a human label, and pre-filled `thickness`/`size` strings ready to
 * drop into a PO line item.
 */
function buildVariantRows(product, category) {
  const thicknesses = product?.thicknessesArr || [];
  const sizes = product?.sizesArr || [];
  const rows = [];
  if (PLYWOOD_STYLE.has(category) && thicknesses.length > 0 && sizes.length > 0) {
    // Cartesian product: thickness × size
    for (const t of thicknesses) {
      for (const s of sizes) {
        rows.push({ key: `${t}|${s}`, label: `${t}  ×  ${s}`, thickness: t, size: s });
      }
    }
  } else if (WEIGHT_BASED.has(category) && thicknesses.length > 0) {
    // Adhesives & Screws — variants are weights (stored in `thickness`)
    for (const t of thicknesses) {
      rows.push({ key: t, label: t, thickness: t, size: '' });
    }
  } else if (sizes.length > 0) {
    // Hardware / Laminates / Veneers / Highlighters — variants are sizes
    for (const s of sizes) {
      rows.push({ key: s, label: s, thickness: '', size: s });
    }
  } else if (thicknesses.length > 0) {
    // Fallback — thickness-only product (rare)
    for (const t of thicknesses) {
      rows.push({ key: t, label: t, thickness: t, size: '' });
    }
  } else {
    // Single-variant product (e.g., a one-off item with no thickness/size axis)
    rows.push({ key: '__single__', label: product?.product_name || '(single variant)', thickness: '', size: '' });
  }
  return rows;
}

export default function PurchaseOrderPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create/Edit PO Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPO, setEditingPO] = useState(null); // null = create, object = edit
  const [supplierName, setSupplierName] = useState('');
  const [poItems, setPoItems] = useState([]);
  const [notes, setNotes] = useState('');
  // po_date is a plain YYYY-MM-DD string. Prefilled to today IST for
  // a new PO, and to the existing po_date (or today) when editing.
  const [poDate, setPoDate] = useState('');
  // Revision fields — required on edit only, hidden on create.
  const [revisionType, setRevisionType] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewingRevisions, setViewingRevisions] = useState(null);
  
  // Add Item Dialog
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  // Product Selection Step (for size/thickness selection)
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Cart-style picker state — mirrors the customer-facing ProductsPage flow:
  // pick a thickness chip, pick a size chip, type qty, tap Add to Selection.
  // Repeat. Then tap "Add N items to PO" to commit the whole buffer at once.
  const [pickedThickness, setPickedThickness] = useState('');
  const [pickedSize, setPickedSize] = useState('');
  const [pickedQtyInput, setPickedQtyInput] = useState('1');
  const [selectionBuffer, setSelectionBuffer] = useState([]);

  // Check access - allow admin, manager, stock_manager
  const canAccess = can('purchase_orders.manage');

  useEffect(() => {
    if (user && !canAccess) {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, canAccess, navigate]);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await api.getPurchaseOrders(params);
      setPurchaseOrders(res.data.purchase_orders || []);
    } catch (err) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter]);

  // Debounced fetch — single effect keyed on the memoized callback so
  // search typing is debounced and filter changes don't double-fetch.
  useEffect(() => {
    const timer = setTimeout(fetchPurchaseOrders, 300);
    return () => clearTimeout(timer);
  }, [fetchPurchaseOrders]);

  const fetchProductsByCategory = async (category) => {
    setLoadingProducts(true);
    try {
      const res = await api.getProducts({ category, limit: 500 });
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setSelectedProduct(null);
    setPickedThickness('');
    setPickedSize('');
    setPickedQtyInput('1');
    setSelectionBuffer([]);
    fetchProductsByCategory(cat);
  };

  // Get unique product variants by brand + subcategory + grade
  // Returns all unique "products" where each variant is a unique combination
  const getUniqueProducts = () => {
    const seen = new Map();
    
    products.forEach(p => {
      // Create a unique key for brand + subcategory + grade combination
      const key = `${p.brand_name || ''}|${p.subcategory || ''}|${p.grade || ''}`;
      
      if (!seen.has(key)) {
        seen.set(key, {
          ...p,
          key,
          thicknesses: new Set(),
          sizes: new Set()
        });
      }
      
      // Add thickness and size to this variant
      const variant = seen.get(key);
      if (p.thickness) variant.thicknesses.add(p.thickness);
      if (p.size) variant.sizes.add(p.size);
      
      // Also include available_sizes array (used by Doors)
      if (p.available_sizes && Array.isArray(p.available_sizes)) {
        p.available_sizes.forEach(s => variant.sizes.add(s));
      }
    });
    
    // Convert to array with sorted thicknesses/sizes
    return Array.from(seen.values()).map(v => ({
      ...v,
      thicknessesArr: [...v.thicknesses].sort((a, b) => {
        // Sort numerically if possible
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      }),
      sizesArr: [...v.sizes].sort((a, b) => {
        // Sort sizes: try to extract first number for comparison
        const getFirstNum = (s) => parseInt(String(s).split(/[x×]/i)[0]) || 0;
        return getFirstNum(b) - getFirstNum(a); // Larger sizes first
      })
    }));
  };

  const unitFromLabel = (label) => {
    const s = (label || '').toLowerCase();
    if (/\bkg\b|\bkilo/.test(s) || /\d\s*kg\b/.test(s)) return 'KG';
    if (/\bltr\b|liter|litre/.test(s)) return 'LTR';
    if (/\bg\b|gram/.test(s) && !/kg/.test(s)) return 'GM';
    if (/\bml\b/.test(s)) return 'ML';
    return null;
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    // Pre-pick the FIRST available thickness/size — matches the customer-cart
    // pattern where the dialog opens already-configured for one variant.
    setPickedThickness(product.thicknessesArr?.[0] || product.thickness || '');
    setPickedSize(product.sizesArr?.[0] || product.size || '');
    setPickedQtyInput('1');
    setSelectionBuffer([]);
  };

  /** Add the currently picked (thickness, size, qty) row into the in-dialog
   *  "selection buffer". User can then change thickness/size/qty and add
   *  another row, repeating until they're done — then "Add N items to PO". */
  const addToSelection = () => {
    if (!selectedProduct) return;
    const qty = parseFloat(pickedQtyInput);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    const isWeight = WEIGHT_BASED.has(selectedCategory);
    const productUnit = rateTypeToUnit(selectedProduct.rate_type);
    const unit = (isWeight && unitFromLabel(pickedThickness)) || productUnit;
    const label = [pickedThickness, pickedSize].filter(Boolean).join(' × ') || '(single)';
    const key = `${pickedThickness || '_'}|${pickedSize || '_'}`;
    setSelectionBuffer((buf) => {
      const idx = buf.findIndex(b => b.key === key);
      if (idx >= 0) {
        const upd = [...buf];
        upd[idx] = { ...upd[idx], quantity: qty };
        toast.success(`Updated ${label} to ${qty} ${unit}`);
        return upd;
      }
      toast.success(`Added ${label} (${qty} ${unit})`);
      return [...buf, { key, thickness: pickedThickness, size: pickedSize, quantity: qty, unit, label }];
    });
    // Reset qty to 1 so next add is a fresh start (size/thickness stays so
    // staff can quickly bump qty for the same size group).
    setPickedQtyInput('1');
  };

  const removeFromSelection = (idx) => {
    setSelectionBuffer((buf) => buf.filter((_, i) => i !== idx));
  };

  /** Commit every row in the selection buffer to the PO at once. */
  const commitSelectionToPO = () => {
    if (!selectedProduct || selectionBuffer.length === 0) return;
    const toAdd = [];
    const duplicates = [];
    for (const row of selectionBuffer) {
      const existing = poItems.find(it =>
        it.brand_name === (selectedProduct.brand_name || '') &&
        it.subcategory === (selectedProduct.subcategory || '') &&
        it.thickness === row.thickness &&
        it.size === row.size &&
        it.main_category === (selectedProduct.main_category || selectedCategory)
      );
      if (existing) { duplicates.push(row.label); continue; }
      toAdd.push({
        brand_name: selectedProduct.brand_name || '',
        product_name: selectedProduct.product_name || selectedProduct.subcategory || '',
        main_category: selectedProduct.main_category || selectedCategory,
        subcategory: selectedProduct.subcategory || '',
        grade: selectedProduct.grade || '',
        brand_description: selectedProduct.brand_description || '',
        thickness: row.thickness,
        size: row.size,
        quantity: row.quantity,
        unit: row.unit,
      });
    }
    if (toAdd.length > 0) {
      setPoItems([...poItems, ...toAdd]);
      toast.success(`Added ${toAdd.length} item${toAdd.length > 1 ? 's' : ''} to PO`);
    }
    if (duplicates.length > 0) {
      toast.error(`Already in PO: ${duplicates.slice(0, 3).join(', ')}${duplicates.length > 3 ? '…' : ''}`);
    }
    resetAddItemDialog();
  };

  const resetAddItemDialog = () => {
    setShowAddItemDialog(false);
    setSelectedCategory('');
    setSelectedProduct(null);
    setProducts([]);
    setProductSearch('');
    setPickedThickness('');
    setPickedSize('');
    setPickedQtyInput('1');
    setSelectionBuffer([]);
  };

  const addCustomItem = () => {
    const newItem = {
      brand_name: '',
      product_name: '',
      main_category: selectedCategory || 'Others',
      subcategory: '',
      thickness: '',
      size: '',
      quantity: 1,
      unit: 'NOS'
    };
    setPoItems([...poItems, newItem]);
    resetAddItemDialog();
  };

  const updateItemField = (index, field, value) => {
    const updated = [...poItems];
    updated[index][field] = value;
    setPoItems(updated);
  };

  const removeItem = (index) => {
    setPoItems(poItems.filter((_, i) => i !== index));
  };

  const handleSavePO = async () => {
    if (!supplierName.trim()) {
      toast.error('Please enter supplier name');
      return;
    }
    if (poItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSaving(true);
    try {
      if (editingPO) {
        // Update existing PO — revision_type is required on the API.
        if (!revisionType) {
          toast.error('Please select what was revised');
          setSaving(false);
          return;
        }
        await api.updatePurchaseOrder(editingPO.id, {
          supplier_name: supplierName,
          items: poItems,
          notes: notes,
          po_date: poDate || undefined,
          revision_type: revisionType,
          revision_note: revisionNote || '',
        });
        toast.success(`Purchase Order ${editingPO.po_number} updated!`);
      } else {
        // Create new PO
        const res = await api.createPurchaseOrder({
          supplier_name: supplierName,
          items: poItems,
          notes: notes,
          po_date: poDate || undefined,
        });
        toast.success(`Purchase Order ${res.data.po_number} created!`);
      }
      setShowCreateDialog(false);
      resetCreateForm();
      fetchPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save PO');
    } finally {
      setSaving(false);
    }
  };

  const resetCreateForm = () => {
    setSupplierName('');
    setPoItems([]);
    setNotes('');
    setEditingPO(null);
    setSelectedCategory('');
    setProducts([]);
    // Prefill po_date to today IST for the create flow; edit overrides
    // it inside openEditDialog.
    const today = new Date();
    const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
    const istToday = new Date(today.getTime() + istOffsetMs - today.getTimezoneOffset() * 60 * 1000);
    setPoDate(istToday.toISOString().slice(0, 10));
    setRevisionType('');
    setRevisionNote('');
  };

  const openEditDialog = async (po) => {
    setEditingPO(po);
    setSupplierName(po.supplier_name || '');
    setPoItems(po.items || []);
    setNotes(po.notes || '');
    // Fall back to today when the legacy PO has no po_date stored.
    if (po.po_date) {
      setPoDate(po.po_date);
    } else {
      const today = new Date();
      const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
      const istToday = new Date(today.getTime() + istOffsetMs - today.getTimezoneOffset() * 60 * 1000);
      setPoDate(istToday.toISOString().slice(0, 10));
    }
    setRevisionType('');
    setRevisionNote('');
    setShowCreateDialog(true);
  };

  const openCreateDialog = () => {
    resetCreateForm();
    setShowCreateDialog(true);
  };

  const downloadPdf = async (po) => {
    try {
      const res = await api.downloadPOPdf(po.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${po.po_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const deletePO = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}?`)) return;
    try {
      await api.deletePurchaseOrder(po.id);
      toast.success('Purchase order deleted');
      fetchPurchaseOrders();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Get unique products with their variants
  const uniqueProducts = getUniqueProducts();
  
  // Filter products by search (brand, subcategory, grade, product_name)
  const filteredUniqueProducts = uniqueProducts.filter(p => {
    if (!productSearch) return true;
    const search = productSearch.toLowerCase();
    return (
      (p.brand_name || '').toLowerCase().includes(search) ||
      (p.product_name || '').toLowerCase().includes(search) ||
      (p.subcategory || '').toLowerCase().includes(search) ||
      (p.grade || '').toLowerCase().includes(search) ||
      (p.brand_description || '').toLowerCase().includes(search)
    );
  });

  const statusColors = {
    created:             'bg-sky-50 text-sky-700',
    sent:                'bg-amber-50 text-amber-700',
    partially_received:  'bg-amber-50 text-amber-700',
    received:            'bg-emerald-50 text-emerald-700',
    cancelled:           'bg-red-50 text-red-700'
  };

  // Human label per status — the underscored raw value shouldn't
  // leak into the UI.
  const statusLabels = {
    created:             'Created',
    sent:                'Sent',
    partially_received:  'Partially Received',
    received:            'Received',
    cancelled:           'Cancelled',
  };

  // Same permission the backend checks (purchase_orders.manage). Everyone
  // else sees the badge without the Select.
  const canEditPO = can('purchase_orders.manage');

  const handleStatusChange = async (po, newStatus) => {
    if (newStatus === po.status) return;
    let note = '';
    if (newStatus === 'partially_received' || newStatus === 'cancelled') {
      // Simple browser prompt — matches the destructive-action pattern
      // already used elsewhere on this page (window.confirm for delete).
      const prompt_label = newStatus === 'partially_received'
        ? 'What was received? (optional)'
        : 'Reason for cancellation? (optional)';
      const entered = window.prompt(prompt_label, '');
      if (entered === null) return;  // user hit Cancel — abort the change
      note = entered;
    }
    try {
      await api.updatePOStatus(po.id, newStatus, note);
      toast.success(`Status set to ${statusLabels[newStatus] || newStatus}`);
      fetchPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="pb-6" data-testid="purchase-order-page">
      {/* Header */}
      <div className="heda-card mb-4">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-heda-olive-tint flex items-center justify-center">
                <FileText className="w-5 h-5 text-heda-olive-dark" />
              </div>
              <div>
                <h1 className="font-display text-[20px] font-bold text-heda-ink">Purchase Orders</h1>
                <p className="text-xs text-heda-mute">Create and manage stock purchase orders</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="rounded-xl bg-heda-ink hover:bg-black"
              data-testid="create-po-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New PO
            </Button>
          </div>

          {/* Search + status filter */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
              <Input
                placeholder="Search by PO number or supplier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl pl-9 bg-heda-cream/50 border-heda-line"
                data-testid="po-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-xl w-full sm:w-52 bg-heda-cream/50 border-heda-line" data-testid="po-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partially_received">Partially Received</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* PO List */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-heda-gold border-t-transparent rounded-full" />
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-heda-mute mx-auto mb-3" />
            <p className="text-heda-sub">No purchase orders found</p>
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              className="rounded-xl mt-4 bg-heda-ink hover:bg-black"
            >
              Create First PO
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {purchaseOrders.map(po => (
              <Card key={po.id} className="rounded-2xl bg-white shadow-none hover:shadow-md transition-shadow" data-testid={`po-card-${po.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-mono text-sm font-bold text-heda-olive-dark">{po.po_number}</p>
                      <p className="text-xs text-heda-mute">
                        {/* Prefer editable po_date; fall back to
                            created_at for legacy PODs saved before
                            po_date existed. */}
                        {po.po_date
                          ? new Date(po.po_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : formatISTDate(po.created_at)}
                      </p>
                      {(po.revision_count || 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => setViewingRevisions(po)}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-heda-line bg-heda-cream/60 text-heda-sub hover:bg-heda-cream/60"
                          data-testid={`po-revised-badge-${po.id}`}
                        >
                          <History className="w-3 h-3" /> Revised ×{po.revision_count}
                        </button>
                      )}
                    </div>
                    <Badge className={statusColors[po.status] || statusColors.created}>
                      {statusLabels[po.status] || (po.status?.charAt(0).toUpperCase() + po.status?.slice(1))}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-heda-mute" />
                    <span className="font-medium text-heda-ink">{po.supplier_name}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-3 text-xs text-heda-mute">
                    <Package className="w-3 h-3" />
                    <span>{po.items?.length || 0} items</span>
                    <span>•</span>
                    <User className="w-3 h-3" />
                    <span>{po.created_by_name}</span>
                  </div>

                  {canEditPO && (
                    <div className="mb-3">
                      <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Status</Label>
                      <Select
                        value={po.status || 'created'}
                        onValueChange={(v) => handleStatusChange(po, v)}
                      >
                        <SelectTrigger className="rounded-xl mt-1 h-8 text-xs bg-white border-heda-line" data-testid={`po-status-select-${po.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created">Created</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="partially_received">Partially Received</SelectItem>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex gap-2 pt-3 border-t border-heda-line">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(po)}
                      className="rounded-xl flex-1 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                      data-testid={`edit-po-${po.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(po)}
                      className="rounded-xl flex-1 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deletePO(po)}
                      className="rounded-xl text-red-700 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit PO Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); resetCreateForm(); } else { setShowCreateDialog(true); }}}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" aria-describedby="create-po-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-heda-gold" />
              {editingPO ? `Edit Purchase Order - ${editingPO.po_number}` : 'Create Purchase Order'}
            </DialogTitle>
          </DialogHeader>
          <p id="create-po-description" className="sr-only">
            {editingPO ? 'Edit purchase order details' : 'Create a new purchase order by entering supplier name and adding items to order.'}
          </p>

          <div className="space-y-4">
            {/* Supplier Name */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Supplier Name *</Label>
              <Input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Enter supplier/vendor name"
                className="rounded-xl bg-heda-cream/50 mt-1"
                data-testid="supplier-name-input"
              />
            </div>

            {/* PO Date — plain YYYY-MM-DD, editable. Prefilled to
                today IST on create and to the current po_date on
                edit (or today IST when a legacy PO has none). */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">PO Date *</Label>
              <Input
                type="date"
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                className="rounded-xl bg-heda-cream/50 mt-1"
                data-testid="po-date-input"
              />
            </div>

            {/* Revision fields — edit flow only. */}
            {editingPO && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl bg-heda-cream border border-heda-line">
                <div>
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">What was revised? *</Label>
                  <Select value={revisionType} onValueChange={setRevisionType}>
                    <SelectTrigger className="rounded-xl bg-heda-cream/50 mt-1" data-testid="revision-type-select">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Date">Date</SelectItem>
                      <SelectItem value="Quantity">Quantity</SelectItem>
                      <SelectItem value="Items">Items</SelectItem>
                      <SelectItem value="Supplier">Supplier</SelectItem>
                      <SelectItem value="Notes">Notes</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Note (optional)</Label>
                  <Input
                    value={revisionNote}
                    onChange={(e) => setRevisionNote(e.target.value)}
                    placeholder="Short reason…"
                    className="rounded-xl bg-heda-cream/50 mt-1"
                    data-testid="revision-note-input"
                  />
                </div>
              </div>
            )}

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Items ({poItems.length})</Label>
                <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddItemDialog(true)}
                  data-testid="add-po-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {poItems.length === 0 ? (
                <div className="text-center py-8 bg-heda-cream rounded-xl border border-dashed border-heda-line">
                  <Package className="w-8 h-8 text-heda-mute mx-auto mb-2" />
                  <p className="text-sm text-heda-mute">No items added yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {poItems.map((item, idx) => (
                    <div key={item.id || item.product_id || `po-${idx}`} className="bg-heda-cream rounded-xl p-3 border border-heda-line">
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="outline" className="rounded-full text-xs">
                          {item.main_category}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(idx)}
                          className="rounded-xl h-6 w-6 p-0 text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Brand</Label>
                          <Input
                            value={item.brand_name}
                            onChange={(e) => updateItemField(idx, 'brand_name', e.target.value)}
                            className="rounded-xl bg-heda-cream/50 h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Product</Label>
                          <Input
                            value={item.product_name}
                            onChange={(e) => updateItemField(idx, 'product_name', e.target.value)}
                            className="rounded-xl bg-heda-cream/50 h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Thickness</Label>
                          <Input
                            value={item.thickness}
                            onChange={(e) => updateItemField(idx, 'thickness', e.target.value)}
                            className="rounded-xl bg-heda-cream/50 h-8 text-sm"
                            placeholder="e.g., 18MM"
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Size</Label>
                          <Input
                            value={item.size}
                            onChange={(e) => updateItemField(idx, 'size', e.target.value)}
                            className="rounded-xl bg-heda-cream/50 h-8 text-sm"
                            placeholder="e.g., 8x4"
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Quantity</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.quantity === 0 || item.quantity === '' ? '' : String(item.quantity)}
                            onChange={(e) => {
                              // String-backed input: allow empty + decimal.
                              // This fixes the bug where typing "25" became "125"
                              // because the controlled value was sticky-prefixed
                              // with 1 on every keystroke.
                              const raw = e.target.value.replace(/[^0-9.]/g, '');
                              updateItemField(idx, 'quantity', raw === '' ? '' : raw);
                            }}
                            onBlur={(e) => {
                              const n = parseFloat(e.target.value);
                              updateItemField(idx, 'quantity', Number.isFinite(n) && n > 0 ? n : 1);
                            }}
                            placeholder="1"
                            className="rounded-xl bg-heda-cream/50 h-8 text-sm"
                            data-testid={`po-item-qty-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">Unit</Label>
                          <Select value={item.unit || 'NOS'} onValueChange={(v) => updateItemField(idx, 'unit', v)}>
                            <SelectTrigger className="rounded-xl bg-heda-cream/50 h-8 text-sm" data-testid={`po-item-unit-${idx}`}>
                              <SelectValue placeholder={item.unit || 'NOS'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NOS">NOS</SelectItem>
                              <SelectItem value="SHT">SHT</SelectItem>
                              <SelectItem value="SET">SET</SelectItem>
                              <SelectItem value="KG">KG</SelectItem>
                              <SelectItem value="LTR">LTR</SelectItem>
                              <SelectItem value="BOX">BOX</SelectItem>
                              <SelectItem value="BDL">BDL</SelectItem>
                              <SelectItem value="SQ FT">SQ FT</SelectItem>
                              <SelectItem value="RFT">RFT</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                className="rounded-xl bg-heda-cream/50 mt-1"
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-heda-line">
              <Button
                variant="outline"
                onClick={() => { setShowCreateDialog(false); resetCreateForm(); }}
                className="rounded-xl flex-1 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePO}
                disabled={saving || !supplierName.trim() || poItems.length === 0}
                className="rounded-xl flex-1 bg-heda-ink hover:bg-black"
                data-testid="save-po-submit"
              >
                {saving ? 'Saving...' : editingPO ? 'Update PO' : 'Create PO'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" aria-describedby="add-item-description">
          <DialogHeader>
            <DialogTitle>Add Item to PO</DialogTitle>
          </DialogHeader>
          <p id="add-item-description" className="sr-only">Select a product category and search for items to add to this purchase order.</p>

          <div className="space-y-4">
            {/* Category Selection */}
            {!selectedCategory ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {STOCK_CATEGORIES.map(cat => (
                  <Button
                    key={cat}
                    variant="outline"
                    onClick={() => handleCategorySelect(cat)}
                    className="rounded-xl h-12 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            ) : selectedProduct ? (
              /* CART-STYLE PICKER — pick a thickness chip, pick a size chip,
                 type qty, "Add to Selection", repeat. Mirror of the
                 customer-facing ProductsPage flow so staff don't have to learn
                 two patterns. */
              (() => {
                const isWeight = WEIGHT_BASED.has(selectedCategory);
                const isPlywoodStyle = PLYWOOD_STYLE.has(selectedCategory);
                const thicknesses = selectedProduct.thicknessesArr || [];
                const sizes = selectedProduct.sizesArr || [];
                const productUnit = rateTypeToUnit(selectedProduct.rate_type);
                const currentUnit = (isWeight && unitFromLabel(pickedThickness)) || productUnit;
                const thicknessLabel = isWeight ? 'Weight' : 'Thickness';
                const totalBufferQty = selectionBuffer.reduce((s, r) => s + (r.quantity || 0), 0);
                return (
                  <div className="space-y-3" data-testid="po-cart-style-picker">
                    {/* Product header */}
                    <div className="flex items-start justify-between gap-2 sticky top-0 bg-white pt-1 pb-2 -mx-1 px-1 border-b border-heda-line z-10">
                      <div className="min-w-0 flex-1">
                        <Badge className="rounded-full bg-heda-olive-tint text-heda-olive-dark">{selectedCategory}</Badge>
                        <p className="font-medium mt-1.5 text-base break-words">{selectedProduct.brand_name}</p>
                        {selectedProduct.subcategory && (
                          <p className="text-xs font-medium text-heda-olive-dark break-words">{selectedProduct.subcategory}</p>
                        )}
                        {(selectedProduct.grade || selectedProduct.brand_description) && (
                          <p className="text-[11px] text-heda-mute break-words">
                            {selectedProduct.grade && <span>Grade: {selectedProduct.grade}</span>}
                            {selectedProduct.grade && selectedProduct.brand_description && ' • '}
                            {selectedProduct.brand_description && <span>{selectedProduct.brand_description}</span>}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedProduct(null); setSelectionBuffer([]); }}
                        className="rounded-xl shrink-0"
                        data-testid="po-change-product-btn"
                      >
                        Change
                      </Button>
                    </div>

                    {/* Thickness/Weight chips */}
                    {thicknesses.length > 0 && (
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-1.5 block">Select {thicknessLabel}</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {thicknesses.map(t => (
                            <Button
                              key={t}
                              variant={pickedThickness === t ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setPickedThickness(t)}
                              className={pickedThickness === t ? 'bg-heda-ink hover:bg-black h-9 px-3' : 'h-9 px-3'}
                              data-testid={`po-thickness-${t}`}
                            >
                              {t}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Size chips — skip for weight-based (adhesives/screws) */}
                    {sizes.length > 0 && !isWeight && (
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-1.5 block">Select Size</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {sizes.map(s => (
                            <Button
                              key={s}
                              variant={pickedSize === s ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setPickedSize(s)}
                              className={pickedSize === s ? 'bg-heda-ink hover:bg-black h-9 px-3' : 'h-9 px-3'}
                              data-testid={`po-size-${s}`}
                            >
                              {s}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* If a Plywood-style product has NO saved sizes, let staff type one */}
                    {isPlywoodStyle && sizes.length === 0 && (
                      <div>
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-1.5 block">Size</Label>
                        <Input
                          value={pickedSize}
                          onChange={(e) => setPickedSize(e.target.value)}
                          placeholder="e.g., 8x4"
                          className="rounded-xl bg-heda-cream/50 h-9"
                        />
                      </div>
                    )}

                    {/* Qty + Add to Selection */}
                    <div className="flex items-end gap-2 pt-1">
                      <div className="flex-1">
                        <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute mb-1 block">Quantity ({currentUnit})</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={pickedQtyInput}
                          onChange={(e) => setPickedQtyInput(e.target.value.replace(/[^0-9.]/g, ''))}
                          onFocus={(e) => e.target.select()}
                          className="rounded-xl bg-heda-cream/50 h-9"
                          data-testid="po-picker-qty-input"
                        />
                      </div>
                      <Button
                        onClick={addToSelection}
                        className="rounded-xl bg-heda-ink hover:bg-black text-white h-9"
                        disabled={
                          (thicknesses.length > 0 && !pickedThickness) ||
                          (sizes.length > 0 && !isWeight && !pickedSize)
                        }
                        data-testid="po-add-to-selection-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add to Selection
                      </Button>
                    </div>

                    {/* Running selection — the in-dialog "mini cart" */}
                    {selectionBuffer.length > 0 && (
                      <div className="border border-heda-line rounded-xl bg-heda-gold-tint p-2" data-testid="po-selection-buffer">
                        <p className="text-xs font-semibold text-heda-sub mb-1.5">
                          Selection ({selectionBuffer.length} — total {totalBufferQty})
                        </p>
                        <div className="space-y-1 max-h-[20vh] overflow-y-auto">
                          {selectionBuffer.map((row, idx) => (
                            <div key={`${row.key}-${idx}`} className="flex items-center justify-between text-sm bg-white rounded-lg px-2 py-1.5">
                              <span className="truncate font-medium">{row.label}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-heda-olive-dark font-semibold">{row.quantity} {row.unit}</span>
                                <button
                                  type="button"
                                  onClick={() => removeFromSelection(idx)}
                                  className="text-red-700 hover:text-red-700 p-0.5"
                                  aria-label={`Remove ${row.label}`}
                                  data-testid={`po-buffer-remove-${idx}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Final commit */}
                    <Button
                      onClick={commitSelectionToPO}
                      disabled={selectionBuffer.length === 0}
                      className="rounded-xl w-full bg-heda-ink hover:bg-black h-10"
                      data-testid="po-commit-selection-btn"
                    >
                      <Plus className="w-4 h-4 mr-1.5" />
                      Add {selectionBuffer.length > 0 ? `${selectionBuffer.length} item${selectionBuffer.length > 1 ? 's' : ''}` : ''} to PO
                    </Button>
                  </div>
                );
              })()
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 sticky top-0 bg-white pt-1 pb-2 -mx-1 px-1 border-b border-heda-line z-10">
                  <Badge className="rounded-full bg-heda-olive-tint text-heda-olive-dark shrink-0">{selectedCategory}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedCategory(''); setProducts([]); setSelectedProduct(null); }}
                    className="rounded-xl shrink-0"
                    data-testid="po-change-category-btn"
                  >
                    Change
                  </Button>
                </div>

                {/* Search Products */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
                  <Input
                    placeholder="Search by brand, subcategory, grade..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="rounded-xl bg-heda-cream/50 pl-9"
                  />
                </div>

                {/* Products List */}
                {loadingProducts ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-heda-gold border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="max-h-[50vh] overflow-y-auto space-y-1">
                    {/* Custom Item Option */}
                    <Button
                      variant="outline"
                      onClick={addCustomItem}
                      className="rounded-xl w-full justify-start text-left h-auto py-2 border-dashed"
                    >
                      <Plus className="w-4 h-4 mr-2 shrink-0" />
                      <span className="truncate">Add Custom Item (Enter details manually)</span>
                    </Button>

                    {/* Show each unique product (brand + subcategory + grade) */}
                    {filteredUniqueProducts.slice(0, 50).map((product) => (
                      <Button
                        key={product.key}
                        variant="ghost"
                        onClick={() => handleProductSelect(product)}
                        className="rounded-xl w-full justify-start text-left h-auto py-2 hover:bg-heda-cream whitespace-normal"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm break-words">
                            {product.brand_name}
                            {product.subcategory && ` - ${product.subcategory}`}
                          </p>
                          <p className="text-xs text-heda-mute break-words whitespace-normal">
                            {product.grade && <span className="font-medium">[{product.grade}] </span>}
                            {product.brand_description && <span>{product.brand_description} • </span>}
                            {product.thicknessesArr?.length > 0 && `${product.thicknessesArr.length} thickness${product.thicknessesArr.length > 1 ? 'es' : ''}`}
                            {product.sizesArr?.length > 0 && ` • ${product.sizesArr.length} size${product.sizesArr.length > 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-heda-mute shrink-0 ml-2" />
                      </Button>
                    ))}

                    {filteredUniqueProducts.length === 0 && !loadingProducts && (
                      <p className="text-center text-sm text-heda-mute py-4">No products found</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Revisions dialog — read-only list of every edit made to this PO. */}
      <Dialog open={!!viewingRevisions} onOpenChange={(v) => { if (!v) setViewingRevisions(null); }}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-revisions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-heda-gold" />
              Revision History
              {viewingRevisions?.po_number && (
                <span className="font-mono text-sm text-heda-sub">· {viewingRevisions.po_number}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!viewingRevisions?.revisions?.length && (
              <p className="text-sm text-heda-mute">No revisions recorded.</p>
            )}
            {(viewingRevisions?.revisions || []).map((r, idx) => (
              <div key={idx} className="border border-heda-line rounded-xl p-3 bg-heda-cream" data-testid={`po-revision-entry-${idx}`}>
                <div className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-xs">
                  <span className="uppercase tracking-wide text-heda-mute">When</span>
                  <span className="text-heda-ink">{formatISTDate(r.revised_at)}</span>
                  <span className="uppercase tracking-wide text-heda-mute">By</span>
                  <span className="text-heda-ink">{r.revised_by_name || '—'}</span>
                  <span className="uppercase tracking-wide text-heda-mute">Type</span>
                  <span className="text-heda-ink font-medium">{r.revision_type || '—'}</span>
                  <span className="uppercase tracking-wide text-heda-mute">Note</span>
                  <span className="text-heda-ink break-words">{r.revision_note || '—'}</span>
                  <span className="uppercase tracking-wide text-heda-mute">Previous PO date</span>
                  <span className="text-heda-ink">{r.previous_po_date || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
